import { BridgeComponent } from "@hotwired/hotwire-native-bridge"
import { canvasResize, PHOTO_THUMBNAIL } from "../lib/image_resize"
import { csrfToken, postJson, post } from "../lib/http"
import { uploadThumbnailsInChunks } from "../lib/thumbnail_backfill"

// Bridge controller that uploads photo library metadata to /library_photos/batch_create.
//
// Uses the "selectPhotos" bridge message which presents a native iOS action sheet:
//   • "Select All Photos"  — scans the full library
//   • "Choose Photos"      — opens the system photo picker for manual selection
//   • "Cancel"             — dismisses with no upload
//
// Nothing happens automatically on connect. The user must explicitly tap the button.
export default class extends BridgeComponent {
  static component = "photo-library"

  static targets = ["button", "scanning", "progress", "progressBar", "progressText", "done", "errorMessage"]
  // 0 = public/central library; >0 = upload these photos for that community only.
  // returnUrl = where to go after upload (the community page for a community upload,
  // else the Home feed).
  static values = { communityId: Number, returnUrl: String }

  connect() {
    super.connect()
    // this.bridge is only set when the native bridge is available.
    // On web, super.connect() returns early but JS continues executing here,
    // so we must guard explicitly before revealing the element.
    if (!this.bridge) return
    this.element.classList.remove("hidden")
  }

  // Called when the user taps the upload button.
  // Sends "selectPhotos" to native → shows iOS action sheet.
  upload() {
    this._setState("scanning")

    this.send("selectPhotos", {}, (response) => {
      const photos = response?.data?.photos || response?.photos
      const error  = response?.data?.error  || response?.error

      // "choose_photos" means the user tapped "Choose Photos" in the action sheet.
      // The Swift side signals this via error so we can present the picker after
      // the action sheet finishes dismissing.
      if (error === "choose_photos") {
        this._pickPhotos()
        return
      }

      // Empty array with no error = user cancelled the action sheet.
      if (!error && Array.isArray(photos) && photos.length === 0) {
        this._setState("button")
        return
      }

      if (error) {
        this._handleError(error)
        return
      }

      const list = Array.isArray(photos) ? photos : []

      if (list.length === 0) {
        this._showError("No photos with location data were found in your library.")
        return
      }

      this._uploadBatches(list)
    })
  }

  // ── Private ─────────────────────────────────────────────────────────────

  // Sends "pickPhotos" to open the system photo picker (no action sheet).
  _pickPhotos() {
    this.send("pickPhotos", {}, (response) => {
      const photos = response?.data?.photos || response?.photos
      const error  = response?.data?.error  || response?.error

      // Empty = user cancelled the picker
      if (!error && Array.isArray(photos) && photos.length === 0) {
        this._setState("button")
        return
      }

      if (error) {
        this._handleError(error)
        return
      }

      const list = Array.isArray(photos) ? photos : []

      if (list.length === 0) {
        this._showError("No photos with location data were found in your selection.")
        return
      }

      this._uploadBatches(list)
    })
  }

  async _uploadBatches(photos) {
    this._setState("progress")

    // Large batches keep round-trips low — the server now bulk-upserts each batch
    // in a single query, so a 13k-photo library is ~26 requests instead of ~260.
    const BATCH_SIZE = 500
    const total = photos.length

    for (let i = 0; i < total; i += BATCH_SIZE) {
      const batch = photos.slice(i, i + BATCH_SIZE)
      await this._sendBatch(batch)
      this._updateProgress(Math.min(i + BATCH_SIZE, total), total)
    }

    // All batches in — run matching once over the complete library. Must happen
    // before the thumbnail backfill below, which queries matched photos.
    await this._finalize()

    // Fetch and upload thumbnails for any matched photos that are missing them
    // (e.g. iCloud-only photos that couldn't be thumbnailed during metadata scan).
    await this._backfillMatchThumbnails()

    // For a community upload, say so + go back to that community (where its matches
    // show); otherwise it's the public library → the Home feed.
    const community = this.communityIdValue > 0
    if (community && this.hasDoneTarget) this.doneTarget.textContent = "✓ Uploaded — finding matches…"
    this._setState("done")

    const destination = this.returnUrlValue && this.returnUrlValue.length > 0 ? this.returnUrlValue : "/discoveries"
    setTimeout(() => {
      if (community) {
        // Community upload: Turbo visit dismisses the upload modal + lands on the group.
        if (window.Turbo) window.Turbo.visit(destination, { action: "replace" })
      } else {
        // Home upload: full reload, NOT a Turbo replace-visit. The Home feed is
        // cacheable; a replace-visit can restore a stale/blank snapshot, leaving the
        // Home page white when you navigate away and come back. A full load is fresh.
        window.location.assign(destination)
      }
    }, community ? 1400 : 1000)
  }

  // After matching runs, fetch thumbnails for matched photos that have none
  // (typically iCloud-only photos). Uses the fetchPhoto bridge to download
  // the full image from iCloud, then generates a small thumbnail client-side.
  async _backfillMatchThumbnails() {
    try {
      const res = await fetch("/library_photos/match_missing_thumbnails", {
        headers: { "X-CSRF-Token": csrfToken() }
      })
      const { client_refs } = await res.json()
      await uploadThumbnailsInChunks(client_refs, (ref) => this._fetchPhotoThumbnail(ref))
    } catch (e) {
      // Non-fatal — thumbnails are cosmetic, don't block navigation
      console.warn("[library-upload] backfill thumbnails failed:", e)
    }
  }

  // Fetches a photo via the native bridge and returns a JPEG data URI, or null.
  _fetchPhotoThumbnail(localIdentifier) {
    return new Promise((resolve) => {
      this.send("fetchPhoto", { localIdentifier }, (response) => {
        const data = response?.data || response
        const b64  = data?.base64
        const mime = data?.mimeType || "image/jpeg"
        if (!b64) return resolve(null)
        resolve(canvasResize(`data:${mime};base64,${b64}`, PHOTO_THUMBNAIL))
      })
    })
  }

  async _sendBatch(batch) {
    await postJson("/library_photos/batch_create", { metadata: batch, community_id: this.communityIdValue })
  }

  // Run matching once after all batches are uploaded.
  async _finalize() {
    await postJson("/library_photos/finalize", { community_id: this.communityIdValue })
  }

  _updateProgress(current, total) {
    const pct = Math.round((current / total) * 100)
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${pct}%`
    }
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent =
        `${current.toLocaleString()} of ${total.toLocaleString()} photos scanned`
    }
  }

  _setState(state) {
    const states = ["button", "scanning", "progress", "done", "errorMessage"]
    states.forEach(s => {
      const targetName = `${s}Target`
      const hasTarget  = `has${targetName.charAt(0).toUpperCase()}${targetName.slice(1)}`
      if (this[hasTarget]) {
        this[targetName].classList.toggle("hidden", s !== state)
      }
    })

    // Lock the modal against swipe-dismiss while a scan/upload is running so it
    // can't be interrupted by an accidental swipe-down; unlock when idle/done.
    if (this.bridge) {
      const busy = state === "scanning" || state === "progress"
      this.send(busy ? "lockScreen" : "unlockScreen", {}, () => {})
    }
  }

  _handleError(error) {
    if (error === "permission_limited") {
      this._showError("Please allow access to all your photos (not just selected ones) in Settings, then try again.")
    } else if (error === "permission_denied") {
      this._showError("Photo library access is required. Please enable it in Settings → Privacy → Photos.")
    } else {
      this._showError("Something went wrong. Please try again.")
    }
  }

  _showError(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
    }
    this._setState("errorMessage")
  }
}
